import { QUARTERS } from 'constants/common'
import { ERROR_MESSAGES } from 'constants/messages'
import { TARGET_OPTIONS_TRANSLATE } from 'constants/translate'
import { EKPICategory, EKPIMetricType, EKPITargetType } from 'enums/kpi'
import { dm, findLastNumberIndex } from 'utils/common'
import { QuarterCalculator } from 'utils/kpi'
import * as yup from 'yup'

export const yupEmptyStringOr = (schema: yup.NumberSchema) => {
  return yup.lazy((value) => (value === '' || value === null ? yup.string().default(null).nullable() : schema))
}

const yupEmptyStringOrNumberTest = (options: yup.TestConfig<string | number | undefined, yup.AnyObject>) => {
  return yup.lazy((value) =>
    value === '' || value === null ? yup.string().test(options).default(null).nullable() : yup.number().test(options)
  )
}

export const validateBaselineQuarterValue = (name: string) =>
  yupEmptyStringOrNumberTest({
    name,
    test: (value, ctx) => {
      const { targetType, baselineQ1, baselineQ2, baselineQ3, baselineQ4, metricType, category } = ctx.parent

      if (!metricType || metricType === EKPIMetricType.TARGET) return true

      const qc = new QuarterCalculator(baselineQ1, baselineQ2, baselineQ3, baselineQ4)

      if (!qc.isAtLeastOneQuarterValueEntered()) {
        return ctx.createError({
          message: ERROR_MESSAGES.F_KPI_QUARTER_AT_LEAST_ONE
        })
      }

      if (metricType === EKPIMetricType.STANDARD) {
        const quarter = ctx.path.slice(-1)
        const quarterTarget = ctx.parent[`targetQ${quarter}`]

        if (typeof quarterTarget === 'number' && !Number.isNaN(quarterTarget)) {
          if (typeof value !== 'number' || Number.isNaN(value)) {
            return ctx.createError({
              message: ERROR_MESSAGES.F_TEXT_REQUIRED
            })
          }
          if (category === EKPICategory.REVERSE && value <= quarterTarget) {
            return ctx.createError({
              message: ERROR_MESSAGES.F_KPI_TYPE_BASELINE_ERROR_2
            })
          }
          if (category !== EKPICategory.REVERSE && value >= quarterTarget) {
            return ctx.createError({
              message: ERROR_MESSAGES.F_KPI_TYPE_BASELINE_ERROR_1
            })
          }
        }
      }

      if (value === undefined) return true

      if (category === EKPICategory.COMPLETION) {
        if (!qc.isOnlyOneQuarterValueEntered()) {
          return ctx.createError({
            message: ERROR_MESSAGES.F_KPI_QUARTER_ONLY_ONE
          })
        }

        if (qc.max() !== 100) {
          return ctx.createError({
            message: ERROR_MESSAGES.F_KPI_QUARTER_MUST_BE_100
          })
        }
      }

      if (targetType === EKPITargetType.CUMULATIVE_ROLLING) {
        // For "Cumulative (rolling)" target type, you can leave any quarter blank or enter a positive number in any quarter.
        const index = QUARTERS.findIndex((quarter: string) => name.includes(quarter))
        const baselineQuarter = [baselineQ1, baselineQ2, baselineQ3, baselineQ4]

        if (typeof baselineQuarter[index] !== 'number' || Number.isNaN(baselineQuarter[index])) {
          return true
        }

        if (index >= 0) {
          const preBaselineQuarter = baselineQuarter.slice(0, index)?.filter((baseline) => typeof baseline === 'number')
          const preBaselineIndex = findLastNumberIndex(baselineQuarter.slice(0, index))

          if (baselineQuarter[index] < preBaselineQuarter[preBaselineQuarter.length - 1]) {
            return ctx.createError({
              message: dm(ERROR_MESSAGES.F_KPI_QUARTER_GREATER, {
                type: 'baseline',
                qx: QUARTERS[index],
                qy: QUARTERS[preBaselineIndex]
              })
            })
          }
        }
        return true
      }

      if (targetType === EKPITargetType.CUMULATIVE_QUARTERLY) {
        // For "Cumulative (quarterly)" target type, you can leave any quarter blank or enter a positive number in any quarter.
        return true
      }

      if (targetType === EKPITargetType.ROLLING_AVERAGE && !qc.areQuarterValuesEqual()) {
        const typeOfTarget = TARGET_OPTIONS_TRANSLATE[targetType as EKPITargetType]
        return ctx.createError({
          message: dm(ERROR_MESSAGES.F_KPI_QUARTER_SAME_EQUAL, { typeOfTarget })
        })
      }

      return true
    }
  })

export const validateTargetQuarterValue = (name: string) =>
  yupEmptyStringOrNumberTest({
    name,
    test: (value, ctx) => {
      const { targetType, targetQ1, targetQ2, targetQ3, targetQ4, metricType, category } = ctx.parent

      if (!metricType || metricType === EKPIMetricType.BASELINE) return true

      const qc = new QuarterCalculator(targetQ1, targetQ2, targetQ3, targetQ4)

      if (!qc.isAtLeastOneQuarterValueEntered()) {
        return ctx.createError({
          message: ERROR_MESSAGES.F_KPI_QUARTER_AT_LEAST_ONE
        })
      }

      if (metricType === EKPIMetricType.STANDARD) {
        const quarter = ctx.path.slice(-1)
        const quarterBaseLine = ctx.parent[`baselineQ${quarter}`]
        if (typeof quarterBaseLine === 'number' && !Number.isNaN(quarterBaseLine)) {
          if (typeof value !== 'number' || Number.isNaN(value)) {
            return ctx.createError({
              message: ERROR_MESSAGES.F_TEXT_REQUIRED
            })
          }
          if (category === EKPICategory.REVERSE && value >= quarterBaseLine) {
            return ctx.createError({
              message: ERROR_MESSAGES.F_KPI_TYPE_TARGET_ERROR_2
            })
          }
          if (category !== EKPICategory.REVERSE && value <= quarterBaseLine) {
            return ctx.createError({
              message: ERROR_MESSAGES.F_KPI_TYPE_TARGET_ERROR_1
            })
          }
        }
      }

      if (value === undefined) return true

      if (targetType === EKPITargetType.CUMULATIVE_ROLLING) {
        // For "Cumulative (rolling)" target type, you can leave any quarter blank or enter a positive number in any quarter.
        const index = QUARTERS.findIndex((quarter: string) => name.includes(quarter))
        const targetQuarter = [targetQ1, targetQ2, targetQ3, targetQ4]

        if (typeof targetQuarter[index] !== 'number' || Number.isNaN(targetQuarter[index])) {
          return true
        }

        if (index >= 0) {
          const preTargetQuarter = targetQuarter.slice(0, index)?.filter((target) => typeof target === 'number')
          const preTargetIndex = findLastNumberIndex(targetQuarter.slice(0, index))

          if (targetQuarter[index] < preTargetQuarter[preTargetQuarter.length - 1]) {
            return ctx.createError({
              message: dm(ERROR_MESSAGES.F_KPI_QUARTER_GREATER, {
                type: 'target',
                qx: QUARTERS[index],
                qy: QUARTERS[preTargetIndex]
              })
            })
          }
        }
        return true
      }

      if (targetType === EKPITargetType.CUMULATIVE_QUARTERLY) {
        // For "Cumulative (quarterly)" target type, you can leave any quarter blank or enter a positive number in any quarter.
        return true
      }

      if (targetType === EKPITargetType.ROLLING_AVERAGE && !qc.areQuarterValuesEqual()) {
        const typeOfTarget = TARGET_OPTIONS_TRANSLATE[targetType as EKPITargetType]
        return ctx.createError({
          message: dm(ERROR_MESSAGES.F_KPI_QUARTER_SAME_EQUAL, { typeOfTarget })
        })
      }

      return true
    }
  })
